// src/components/employees/EmployeeForm.jsx
import React, { useState, useEffect } from "react";
import Select from "react-select";
import CheckboxDropdown from "../common/FormElements/CheckboxDropdown";
import { createEmployee } from "../../services/employeeService";
import { getRoleOptions } from "../../utils/permissions";
import "../../styles/ProfileAvatar.css";
import { useToast } from '../Admissionform/ToastManager';

const departmentsList = ["Pumo Tech", "Monz Creative School"];

// react-select custom styles to match your existing form design
const selectStyles = {
  control: (base, state) => ({
    ...base,
    border: "1px solid #ddd",
    borderRadius: "4px",
    boxShadow: state.isFocused ? "0 0 0 1px #474556" : "none",
    borderColor: state.isFocused ? "#474556" : "#ddd",
    fontFamily: "'Times New Roman', Times, serif",
    fontSize: "14px",
    minHeight: "42px",
    "&:hover": { borderColor: "#474556" },
  }),
  menu: (base) => ({
    ...base,
    fontFamily: "'Times New Roman', Times, serif",
    fontSize: "14px",
    zIndex: 9999,
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "#474556"
      : state.isFocused
        ? "#f3f4f6"
        : "white",
    color: state.isSelected ? "white" : "#333",
    fontFamily: "'Times New Roman', Times, serif",
    fontSize: "14px",
    cursor: "pointer",
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: "#ede9fe",
    borderRadius: "4px",
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: "#312f44",
    fontFamily: "'Times New Roman', Times, serif",
    fontSize: "13px",
    fontWeight: "600",
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: "#474556",
    "&:hover": { backgroundColor: "#474556", color: "white" },
  }),
  placeholder: (base) => ({
    ...base,
    color: "#9ca3af",
    fontFamily: "'Times New Roman', Times, serif",
    fontSize: "14px",
  }),
  clearIndicator: (base) => ({
    ...base,
    color: "#9ca3af",
    "&:hover": { color: "#474556" },
  }),
  dropdownIndicator: (base) => ({
    ...base,
    color: "#9ca3af",
    "&:hover": { color: "#474556" },
  }),
};

const EmployeeForm = ({
  branches: allowedBranches = [],
  locations = [],
  userRole,
  onClose,
}) => {
  const [employeeData, setEmployeeData] = useState({
    emp_name: "",
    emp_code: "",
    role: "",
    organization: [],
    Department: [],
    branch: [],
    location: [],
    email_id: "",
    password: "",
    contact_num: "",
    alter_contact: "",
    address: "",
    has_access: false,
  });

  const [allLocationBranches, setAllLocationBranches] = useState([]);
  const [filteredBranches, setFilteredBranches] = useState([]);
  const { showSuccess, showError } = useToast();
  const [departmentOptions, setDepartmentOptions] = useState([]);

  // Convert plain string arrays to react-select option format
  const locationOptions = locations.map((loc) => ({ value: loc, label: loc }));

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/alltech`);
        const data = await res.json();

        // extract unique department names
        const uniqueDepartments = [
          ...new Set(data.data.map((item) => item.Department)),
        ];

        setDepartmentOptions(uniqueDepartments);
      } catch (err) {
        console.error("Failed to fetch departments:", err);
      }
    };

    fetchDepartments();
  }, []);

  const departmentSelectOptions = departmentOptions.map((dept) => ({
    value: dept,
    label: dept,
  }));

  const selectedDepartmentOptions = employeeData.Department.map((dept) => ({
    value: dept,
    label: dept,
  }));
  const handleDepartmentChange = (selectedOptions) => {
    const values = selectedOptions
      ? selectedOptions.map((opt) => opt.value)
      : [];

    setEmployeeData((prev) => ({
      ...prev,
      Department: values,
    }));
  };

  // Fetch full location-branch mapping on mount
  useEffect(() => {
    const fetchLocationBranches = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/location`);
        const data = await res.json();
        setAllLocationBranches(data);
      } catch (err) {
        console.error("Failed to fetch location mapping:", err);
      }
    };
    fetchLocationBranches();
  }, []);

  // Recompute branch options whenever selected locations change
  useEffect(() => {
    if (!employeeData.location.length) {
      setFilteredBranches([]);
      setEmployeeData((prev) => ({ ...prev, branch: [] }));
      return;
    }

    const branchesForSelectedLocations = allLocationBranches
      .filter(
        (row) =>
          employeeData.location.includes(row.location) &&
          allowedBranches.includes(row.branch)
      )
      .map((row) => row.branch);

    const unique = Array.from(new Set(branchesForSelectedLocations));
    setFilteredBranches(unique);

    // Auto-deselect branches that are no longer valid
    setEmployeeData((prev) => ({
      ...prev,
      branch: prev.branch.filter((br) => unique.includes(br)),
    }));
  }, [employeeData.location, allLocationBranches, allowedBranches]);

  // Convert filtered branch strings to react-select options
  const branchOptions = filteredBranches.map((br) => ({
    value: br,
    label: br,
  }));

  // Currently selected location options (for react-select value prop)
  const selectedLocationOptions = employeeData.location.map((loc) => ({
    value: loc,
    label: loc,
  }));

  // Currently selected branch options (for react-select value prop)
  const selectedBranchOptions = employeeData.branch.map((br) => ({
    value: br,
    label: br,
  }));

  // Handler for react-select Location change
  const handleLocationChange = (selectedOptions) => {
    const selectedValues = selectedOptions
      ? selectedOptions.map((opt) => opt.value)
      : [];
    setEmployeeData((prev) => ({ ...prev, location: selectedValues }));
  };

  // Handler for react-select Branch change
  const handleBranchChange = (selectedOptions) => {
    const selectedValues = selectedOptions
      ? selectedOptions.map((opt) => opt.value)
      : [];
    setEmployeeData((prev) => ({ ...prev, branch: selectedValues }));
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEmployeeData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleCheckboxChange = (org) => {
    setEmployeeData((prev) => ({
      ...prev,
      organization: prev.organization.includes(org)
        ? prev.organization.filter((d) => d !== org)
        : [...prev.organization, org],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await createEmployee(employeeData);
      showSuccess("Employee created successfully!");
      onClose();
    } catch (error) {
      showError(
        "Failed to create employee: " +
        (error.response?.data?.message || error.message)
      );
    }
  };

  return (
    <div className="employee-form-modal">
      <div className="employee-form-container">
        <div className="form-header">
          <h2>Create New Employee</h2>
          <button className="close-button" onClick={onClose}>
            ×
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Employee Code:</label>
            <input
              type="text"
              name="emp_code"
              value={employeeData.emp_code}
              onChange={handleInputChange}

            />
          </div>

          <div className="form-group">
            <label>Name:<span className="mandatory">*</span></label>
            <input
              type="text"
              name="emp_name"
              value={employeeData.emp_name}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Role:<span className="mandatory">*</span></label>
            <select
              name="role"
              value={employeeData.role}
              onChange={handleInputChange}
              required
            >
              <option value="">Choose your Role</option>
              {getRoleOptions(userRole).map((role) => (
                <option key={role.value} value={role.value}>
                  {role.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Organization:<span className="mandatory">*</span></label>
            <div className="switch-group">
              {departmentsList.map((dept) => (
                <label key={dept} className="switch-item">
                  <span className="switch-wrapper">
                    <input
                      type="checkbox"
                      className="switch-checkbox"
                      value={dept}
                      checked={employeeData.organization.includes(dept)}
                      onChange={() => handleCheckboxChange(dept)}
                    />
                    <span className="switch-slider"></span>
                  </span>
                  {dept}
                </label>
              ))}
            </div>
          </div>
          <div className="form-group">
            <label>Department:<span className="mandatory">*</span></label>
            <Select
              isMulti
              options={departmentSelectOptions}
              value={selectedDepartmentOptions}
              onChange={handleDepartmentChange}
              placeholder="Select Departments"
              styles={selectStyles}
              classNamePrefix="rs"
              closeMenuOnSelect={false}
            />
          </div>
          {/* ── Location: react-select multi ── */}
          <div className="form-group">
            <label>Location:<span className="mandatory">*</span></label>
            <Select
              isMulti
              options={locationOptions}
              value={selectedLocationOptions}
              onChange={handleLocationChange}
              placeholder="Select Locations"
              styles={selectStyles}
              classNamePrefix="rs"
              closeMenuOnSelect={false}
            />
          </div>

          {/* ── Branch: react-select multi (filtered by location) ── */}
          <div className="form-group">
            <label>Branch:<span className="mandatory">*</span></label>
            <Select
              isMulti
              options={branchOptions}
              value={selectedBranchOptions}
              onChange={handleBranchChange}
              placeholder={
                employeeData.location.length === 0
                  ? "Select a location first"
                  : "Select Branches"
              }
              isDisabled={employeeData.location.length === 0}
              styles={selectStyles}
              classNamePrefix="rs"
              closeMenuOnSelect={false}
              noOptionsMessage={() =>
                employeeData.location.length
                  ? "No branches available for selected locations"
                  : "Select a location first"
              }
            />
          </div>

          {/* <div className="form-group">
            <label className="hasaccess">
              Allow Access:
              <span className="switch1-wrapper">
                <input
                  className="switch-checkbox"
                  type="checkbox"
                  name="has_access"
                  checked={employeeData.has_access}
                  onChange={handleInputChange}
                />
                <span className="switch-slider"></span>
              </span>
            </label>
          </div> */}

          <div className="form-group">
            <label>Email:<span className="mandatory">*</span></label>
            <input
              type="email"
              name="email_id"
              value={employeeData.email_id}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Password:<span className="mandatory">*</span></label>
            <div className="tooltip-container">
              <input
                type="password"
                name="password"
                value={employeeData.password}
                onChange={handleInputChange}
                required
                placeholder="Enter your password"
              />
              <span className="tooltip">
                Password must contain 1 uppercase, 1 lowercase, 1 special
                character, and be 8-16 characters long
              </span>
            </div>
          </div>

          <div className="form-group">
            <label>Contact Number:<span className="mandatory">*</span></label>
            <input
              type="tel"
              name="contact_num"
              value={employeeData.contact_num}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Alternative Contact:</label>
            <input
              type="tel"
              name="alter_contact"
              value={employeeData.alter_contact}
              onChange={handleInputChange}
            />
          </div>

          <div className="form-group">
            <label>Address:<span className="mandatory">*</span></label>
            <textarea
              name="address"
              value={employeeData.address}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-buttons">
            <button type="submit" className="submit-button">
              Create Employee
            </button>
            <button type="button" className="cancel-button" onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EmployeeForm;